import { Box, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useAnalytics from '@/hooks/useAnalytics';
import useSourceConnectorPrefs from '@/hooks/useSourceConnectorPrefs';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import { TOGGLE_SX } from '@/ui-library/switch.styles';

import SourceConnectorMarkView from './SourceConnectorMark';
import {
  connectorDescriptionKey,
  connectorNameKey,
  SOURCE_CONNECTORS,
  type SourceConnectorId,
} from './SourceConnectors';

/** Where the list is shown, for analytics. */
export type SourceConnectorSurface = 'composer' | 'settings';

// The Settings page's switch, unchanged, so a source switch reads like every
// other preference.
const CONNECTOR_SWITCH_SX = [TOGGLE_SX, { flexShrink: 0 }];

/**
 * A green **On** status chip. A status, not a control: every connector is
 * consulted on every answer, so there is nothing to flip, and a chip (rather
 * than a switch) says so at a glance. Assistive tech reads "Always on".
 */
function OnChip({ compact }: { compact: boolean }) {
  const { t } = useTranslation('common');
  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        px: compact ? '7px' : 1,
        py: compact ? '1px' : '3px',
        borderRadius: '999px',
        backgroundColor: semanticColors.successBackground,
        border: `1px solid ${primitiveColors.green3}`,
        color: semanticColors.successAction,
        fontSize: compact ? 10 : 12,
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      <span aria-hidden="true">{t('sourceConnectors.statusOn')}</span>
      <Box
        component="span"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {t('sourceConnectors.statusAlwaysOn')}
      </Box>
    </Box>
  );
}

/**
 * The connector rows shared by the composer's Sources popover and the Settings
 * card: mark, name, one-line description, and either a green **On** chip or a
 * switch.
 *
 * Always-on connectors carry the chip: they are consulted on every answer, and
 * a switch would promise a choice the product does not offer. An optional
 * connector (USAFacts) carries a real switch, on by default; switching it off
 * names it in the next chat request's `disabled_connectors`.
 *
 * `compact` is the composer pop-up's density — a smaller mark and type so the
 * five rows fit beneath the chat box; Settings uses the regular size.
 */
export default function SourceConnectorList({
  compact = false,
  surface,
}: {
  compact?: boolean;
  surface: SourceConnectorSurface;
}) {
  const { t } = useTranslation('common');
  const analytics = useAnalytics();
  const { disabled, setEnabled } = useSourceConnectorPrefs();

  const handleToggle = (id: SourceConnectorId, enabled: boolean) => {
    setEnabled(id, enabled);
    analytics.track('Source Connector Toggled', { connector: id, enabled, surface });
  };

  const markSize = compact ? 24 : 36;
  const nameSize = compact ? 12 : 15;
  const descriptionSize = compact ? 11 : 13;
  const last = SOURCE_CONNECTORS.length - 1;
  return (
    <Box component="ul" role="list" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {SOURCE_CONNECTORS.map((connector, i) => {
        return (
          <Box
            key={connector.id}
            component="li"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: compact ? 1 : 1.5,
              py: compact ? 0.75 : 1.5,
              // The pop-up's own padding closes the box; a last-row gap on top of
              // it read as empty space and pushed the panel into scrolling.
              ...(compact && i === last ? { pb: 0 } : {}),
              borderTop: i > 0 ? `1px solid ${semanticColors.borderLight}` : 'none',
            }}
          >
            <SourceConnectorMarkView mark={connector.mark} size={markSize} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: nameSize,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: semanticColors.textPrimary,
                }}
              >
                {t(connectorNameKey(connector.id))}
              </Typography>
              <Typography
                sx={{
                  fontSize: descriptionSize,
                  lineHeight: 1.4,
                  color: semanticColors.textCaption,
                  mt: '2px',
                }}
              >
                {t(connectorDescriptionKey(connector.id))}
              </Typography>
            </Box>
            {connector.alwaysOn ? (
              <OnChip compact={compact} />
            ) : (
              <Switch
                checked={!disabled.includes(connector.id)}
                onChange={(event) => handleToggle(connector.id, event.target.checked)}
                sx={CONNECTOR_SWITCH_SX}
                slotProps={{
                  input: {
                    'aria-label': t(connectorNameKey(connector.id)),
                    'aria-description': t('sourceConnectors.toggleHint'),
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
